import React, { useState, useEffect, useMemo } from 'react';
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Avatar,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fade,
  Pagination,
  Select,
  MenuItem,
  Alert,
  TableSortLabel,
  TablePagination,
  FormControl,
  InputLabel,
  Autocomplete,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Work as WorkIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import { listStores } from '../../api/stores';
import RecordViewModal from '../../components/RecordViewModal';
import SetRatesModal from '../../components/SetRatesModal';
import DeleteRatesDialog from '../../components/DeleteRatesDialog';
import { getRates, postRates, deleteEmployeeRates } from '../../api/rates';
import { getEmployeeSchedule } from '../../api/timekeep';
import {
  getAllowances,
  createAllowance,
  updateAllowance,
  deleteAllowance,
  assignToPersonnel,
  removePersonnelAssignment
} from '../../api/allowances';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';

const normalizeAccessValue = (value) => String(value || '').trim().toLowerCase();

const normalizeAccessKey = (value) => normalizeAccessValue(value)
  .replace(/\bphilippines\b/g, 'phils')
  .replace(/\bcorporation\b/g, 'corp')
  .replace(/\bincorporated\b/g, 'inc')
  .replace(/[^a-z0-9]/g, '');

const accessMatches = (accessValues, candidate) => {
  const normalizedCandidate = normalizeAccessValue(candidate);
  if (!normalizedCandidate) return false;

  const candidateKey = normalizeAccessKey(candidate);
  return accessValues.some((accessValue) => {
    const accessKey = normalizeAccessKey(accessValue);
    return normalizedCandidate === accessValue ||
      normalizedCandidate.includes(accessValue) ||
      accessValue.includes(normalizedCandidate) ||
      (candidateKey && accessKey && candidateKey === accessKey);
  });
};

// Mock data for personnel
const personnel = [
  { id: 1, name: 'JEFFREY PAREJA', position: 'Cashier', store: 'Sparkle Star International - Inho...', active: true },
  { id: 2, name: 'DARLO MIGUEL ILAGAN', position: 'Manager', store: 'Sparkle Star International - Inho...', active: true },
  { id: 3, name: 'ABBY DOMACENA', position: 'Crew', store: 'Sparkle Star International - Inho...', active: true },
  { id: 4, name: 'RENIE VER BASILIO', position: 'Supervisor', store: 'Sparkle Star International - Inho...', active: true },
  { id: 5, name: 'ANTHONY JOSHUA UY', position: 'Crew', store: 'Sparkle Star International - Inho...', active: true },
];

const Personnel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeSchedule, setEmployeeSchedule] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [fromDate, setFromDate] = useState('2025-09-15');
  const [toDate, setToDate] = useState('2025-09-17');
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [setRatesModalOpen, setSetRatesModalOpen] = useState(false);
  const [ratesEmployee, setRatesEmployee] = useState(null);
  const [deleteRatesDialogOpen, setDeleteRatesDialogOpen] = useState(false);
  const [deleteRatesEmployee, setDeleteRatesEmployee] = useState(null);
  const [employeeDetailsOpen, setEmployeeDetailsOpen] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  
  // Allowances dialog state
  const [allowancesDialogOpen, setAllowancesDialogOpen] = useState(false);
  const [allowances, setAllowances] = useState([]);
  const [employeeAllowances, setEmployeeAllowances] = useState([]);
  const [allowanceDialogOpen, setAllowanceDialogOpen] = useState(false);
  const [allowanceDialogMode, setAllowanceDialogMode] = useState('add');
  const [selectedAllowance, setSelectedAllowance] = useState(null);
  const [allowanceFormData, setAllowanceFormData] = useState({
    name: '',
    type: 'billing',
    amount: 0,
    description: '',
    is_active: true,
    is_taxable: true,
    admin: true
  });
  const [allowanceFormErrors, setAllowanceFormErrors] = useState({});
  const [loadingAllowances, setLoadingAllowances] = useState(false);
  const [allowanceSearchTerm, setAllowanceSearchTerm] = useState('');
  const [savingAllowance, setSavingAllowance] = useState(false);

  // Enhanced table state
  const [page, setPage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [dense, setDense] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super-admin' || user?.role === 'admin';
  const groupAccessValues = useMemo(
    () => (Array.isArray(user?.groupAccess) ? user.groupAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );
  const storeAccessValues = useMemo(
    () => (Array.isArray(user?.storeAccess) ? user.storeAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );

  const userCanAccessStore = (store) => (
    isSuperAdmin ||
    accessMatches(groupAccessValues, store?.group_name || store?.group) ||
    accessMatches(storeAccessValues, store?.store || store?.name)
  );

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return Math.max(0, Number(value)).toLocaleString('en-PH', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Load stores from API
  const loadStores = async () => {
    setStoresLoading(true);
    try {
      const storesResponse = await listStores();
      if (storesResponse.status === 'success') {
        // Transform the data to match the expected format
        const transformedStores = (storesResponse.data.stores || [])
          .filter(userCanAccessStore)
          .map((storeObj, index) => ({
          id: storeObj.id || index + 1,
          _id: storeObj._id,
          name: storeObj.store,
          store: storeObj.store,
          group: storeObj.group_name || 'Unknown Group',
          group_id: storeObj.group_id,
          location: storeObj.location || '',
          active: true,
          user_id: storeObj.user_id,
          created_at: storeObj.created_at,
          updated_at: storeObj.updated_at
        }));

        setStores(transformedStores);

        const currentSelectionAllowed = transformedStores.some((store) => store.name === selectedStore?.name);
        if (transformedStores.length > 0 && (!selectedStore || !currentSelectionAllowed)) {
          setSelectedStore(transformedStores[0]);
          setEmployees([]);
        } else if (transformedStores.length === 0) {
          setSelectedStore(null);
          setEmployees([]);
        }
      }
    } catch (err) {
      console.error('Error loading stores:', err);
      enqueueSnackbar('Failed to load stores', { variant: 'error' });
    } finally {
      setStoresLoading(false);
    }
  };

  // Load stores on component mount
  useEffect(() => {
    loadStores();
  }, [user]);

  // Simulate loading effect
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1200));
        enqueueSnackbar('Personnel loaded successfully', { variant: 'success' });
      } catch (err) {
        setError('Failed to load personnel');
        enqueueSnackbar('Failed to load personnel', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [enqueueSnackbar]);

  // Sorting utility functions - moved to top to avoid initialization issues
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Enhanced filter and search logic
  const filteredPersonnel = useMemo(() => {
    return employees.filter(person => 
      String(person.displayName || person.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(person.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(person.company || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [employees, searchTerm]);

  // Apply sorting
  const sortedPersonnel = useMemo(() => {
    return stableSort(filteredPersonnel, getComparator(order, orderBy));
  }, [filteredPersonnel, order, orderBy]);

  // Apply pagination for enhanced table
  const paginatedSortedPersonnel = useMemo(() => {
    return sortedPersonnel.slice(page * tableRowsPerPage, page * tableRowsPerPage + tableRowsPerPage);
  }, [sortedPersonnel, page, tableRowsPerPage]);

  // Legacy pagination for existing components
  const totalPages = Math.ceil(filteredPersonnel.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedPersonnel = filteredPersonnel.slice(startIndex, startIndex + rowsPerPage);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Refresh stores data
      await loadStores();
      // Simulate refresh
      await new Promise(resolve => setTimeout(resolve, 1000));
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  // Enhanced table handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setTableRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const clearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleExport = (type) => {
    enqueueSnackbar(`Exporting personnel as ${type}...`, { variant: 'info' });
  };

  const handleViewRecord = async (employee) => {
    if (!selectedStore || !stores.some((store) => store.name === selectedStore.name)) {
      enqueueSnackbar('Please select an accessible store first', { variant: 'warning' });
      setEmployees([]);
      return;
    }

    try {
      const result = await axios.post(
        "https://api-admin.sparkletimekeeping.com/api/user/store",
        {
          store: selectedStore.name
        },
        {
          headers: {
            "Content-Type": "application/json",
          }
        }
      );
      if (result.data.length > 0) {
        setEmployees(result.data);
      }
    } catch (err) {
      console.error("API Error:", err.response?.data || err.message);
    }
    
    // setRecordModalOpen(true);
  };

  const handleDisplayEmployeeRecord = async (employee) => {
    console.log("HERE")
    setLoadingSchedule(true);
    setSelectedEmployee(employee);
    try {
      const fromDateFormatted = dayjs(fromDate).format("YYYY-MM-DD");
      const toDateFormatted = dayjs(toDate).format("YYYY-MM-DD");
      const scheduleData = await getEmployeeSchedule(
        employee._id,
        fromDateFormatted,
        toDateFormatted
      );

      if (!scheduleData || scheduleData.length === 0) {
        alert("No records found");
      } else {
        setEmployeeSchedule(scheduleData);
        setEmployeeDetailsOpen(true);
        setLoadingSchedule(false);
      }
    } catch (err) {
      console.error("API Error:", err.response?.data || err.message);
    } finally {
      setLoadingSchedule(false);

    }
  };
  const handleCloseRecordModal = () => {
    setRecordModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleSetRates = async (employee) => {
    if (!selectedStore) {
      enqueueSnackbar('Please select a store first', { variant: 'warning' });
      return;
    }
    else {
      const data = {
        id: employee._id
      }
      const rates = await getRates(data);
      if(rates?.data) {
        setRatesEmployee(rates.data);
        setSelectedEmployee(employee)
      }
    }
    
    setSetRatesModalOpen(true);
  };

  const handleCloseSetRatesModal = () => {
    setSetRatesModalOpen(false);
    setRatesEmployee(null);
  };

  const handleSaveRates = async (rates) => {
    try {
      const { _id, ...rest } = rates;

      const ratesFormDataFormatted = {
        ...rest,
        uid: selectedEmployee._id
      };
      const result = await postRates(ratesFormDataFormatted)
      enqueueSnackbar(`Rates saved for ${selectedEmployee?.displayName}`, { variant: 'success' });
    } catch (error) {
      console.error('Error saving rates:', error);
      enqueueSnackbar(error.message || 'Failed to save rates', { variant: 'error' });
    }
    
  };

  const handleDeleteRates = (employee) => {
    setDeleteRatesEmployee(employee);
    setDeleteRatesDialogOpen(true);
  };

  const handleCloseDeleteRatesDialog = () => {
    setDeleteRatesDialogOpen(false);
    setDeleteRatesEmployee(null);
  };

  const [deletingRates, setDeletingRates] = useState(false);

  const handleConfirmDeleteRates = async (employee) => {
    if (!employee) return;
    const employeeId = employee._id || employee.id;
    if (!employeeId) {
      enqueueSnackbar('Cannot delete rates: employee id missing', { variant: 'error' });
      return;
    }
    try {
      setDeletingRates(true);
      await deleteEmployeeRates(employeeId);
      const displayName = employee?.displayName || employee?.name || 'Employee';
      enqueueSnackbar(`Rates deleted for ${displayName}`, { variant: 'success' });
      handleCloseDeleteRatesDialog();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to delete rates';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setDeletingRates(false);
    }
  };

  // Allowances dialog handlers
  const handleOpenAllowancesDialog = async (employee) => {
    if (!employee) {
      enqueueSnackbar('Please select an employee first', { variant: 'warning' });
      return;
    }
    setSelectedEmployee(employee);
    // await loadAllowances();
    await loadEmployeeAllowances(employee._id || employee.id);
    setAllowancesDialogOpen(true);
  };

  const handleCloseAllowancesDialog = () => {
    setAllowancesDialogOpen(false);
    setAllowanceSearchTerm('');
    setSelectedEmployee(null);
  };

  // Load all allowances
  // const loadAllowances = async () => {
  //   try {
  //     setLoadingAllowances(true);
  //     console.log(selectedEmployee)
  //     const data = {
  //       personnel: employee._id || employee.id
  //     }
  //     const response = await getAllowances(data);
  //     if (response.status === 'success') {
  //       setAllowances(response.data || []);
  //     }
  //   } catch (error) {
  //     console.error('Error loading allowances:', error);
  //     enqueueSnackbar('Failed to load allowances', { variant: 'error' });
  //   } finally {
  //     setLoadingAllowances(false);
  //   }
  // };

  const loadEmployeeAllowances = async (employeeId) => {
    if (!employeeId) return;
    try {
      const data = {
        personnel: employeeId
      }
      const response = await getAllowances(data);
      if (response.status === 'success') {
        const allAllowances = response.data || [];
        // const assignedAllowances = allAllowances.filter(allowance => 
        //   allowance.assignments?.personnel?.includes(employeeId)
        // );
        setEmployeeAllowances(response.data);
      }
    } catch (error) {
      console.error('Error loading employee allowances:', error);
    }
  };

  // Handle allowance dialog open
  const handleOpenAllowanceDialog = (mode = 'add', allowance = null) => {
    setAllowanceDialogMode(mode);
    if (mode === 'edit' && allowance) {
      setSelectedAllowance(allowance);
      setAllowanceFormData({
        name: allowance.name || '',
        type: allowance.type || 'billing',
        amount: allowance.amount || 0,
        description: allowance.description || '',
        is_active: allowance.is_active !== undefined ? allowance.is_active : true,
        is_taxable: allowance.is_taxable !== undefined ? allowance.is_taxable : true,
        admin: allowance.admin !== undefined ? allowance.admin : true,
      });
    } else {
      setSelectedAllowance(null);
      setAllowanceFormData({
        name: '',
        type: 'billing',
        amount: 0,
        description: '',
        is_active: true,
        is_taxable: true,
        admin: true
      });
    }
    setAllowanceFormErrors({});
    setAllowanceDialogOpen(true);
  };

  // Handle allowance dialog close
  const handleCloseAllowanceDialog = () => {
    setAllowanceDialogOpen(false);
    setSelectedAllowance(null);
    setAllowanceFormData({
      name: '',
      type: 'fixed_amount',
      amount: 0,
      rate: 0,
      description: '',
      is_active: true,
      is_taxable: true
    });
    setAllowanceFormErrors({});
  };

  // Validate allowance form
  const validateAllowanceForm = () => {
    const errors = {};
    if (!allowanceFormData.name.trim()) {
      errors.name = 'Allowance name is required';
    }
    if (allowanceFormData.type === 'fixed_amount' && (!allowanceFormData.amount || allowanceFormData.amount <= 0)) {
      errors.amount = 'Amount must be greater than 0';
    }
    if (allowanceFormData.type === 'rate_based' && (!allowanceFormData.rate || allowanceFormData.rate <= 0)) {
      errors.rate = 'Rate must be greater than 0';
    }
    setAllowanceFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle allowance form submit
  const handleAllowanceFormSubmit = async () => {
    if (!validateAllowanceForm()) return;

    setSavingAllowance(true);
    try {
      console.log(selectedEmployee)
      const allowanceData = {
        name: allowanceFormData.name,
        type: allowanceFormData.type,
        amount: allowanceFormData.amount,
        description: allowanceFormData.description,
        is_active: allowanceFormData.is_active,
        is_taxable: allowanceFormData.is_taxable,
        admin: allowanceFormData.admin,
        assignments: {
          personnel: selectedEmployee._id,
          stores: [],
          groups: []
        }
      };

      // if (allowanceFormData.type === 'fixed_amount') {
      //   allowanceData.amount = allowanceFormData.amount;
      // } else {
      //   allowanceData.rate = allowanceFormData.rate;
      // }

      let allowanceId;
      if (allowanceDialogMode === 'add') {
        const response = await createAllowance(allowanceData);
        if (response.status === 'success') {
          allowanceId = response.data._id;
          enqueueSnackbar('Allowance created successfully', { variant: 'success' });
        }
      } else {
        await updateAllowance(selectedAllowance._id, allowanceData);
        allowanceId = selectedAllowance._id;
        enqueueSnackbar('Allowance updated successfully', { variant: 'success' });
      }

      // Assign the allowance to the employee
      if (allowanceId && selectedEmployee) {
        const employeeId = selectedEmployee._id || selectedEmployee.id;
        try {
          await assignToPersonnel(allowanceId, [employeeId]);
        } catch (assignError) {
          console.error('Error assigning allowance to employee:', assignError);
        }
      }

      // await loadEmployeeAllowances(selectedEmployee);
      if (selectedEmployee) {
        const employeeId = selectedEmployee._id || selectedEmployee.id;
        await loadEmployeeAllowances(employeeId);
      }
      handleCloseAllowanceDialog();
    } catch (error) {
      console.error('Error saving allowance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save allowance';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingAllowance(false);
    }
  };

  // Handle delete allowance
  const handleDeleteAllowance = async (allowance) => {
    if (!window.confirm(`Are you sure you want to delete "${allowance.name}"?`)) {
      return;
    }

    try {
      await deleteAllowance(allowance._id);
      enqueueSnackbar('Allowance deleted successfully', { variant: 'success' });
      await loadEmployeeAllowances(selectedEmployee);
      if (selectedEmployee) {
        const employeeId = selectedEmployee._id || selectedEmployee.id;
        await loadEmployeeAllowances(employeeId);
      }
    } catch (error) {
      console.error('Error deleting allowance:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete allowance';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Handle assign/unassign allowance to employee
  const handleToggleAllowanceAssignment = async (allowance) => {
    if (!selectedEmployee) return;

    const employeeId = selectedEmployee._id || selectedEmployee.id;
    const isAssigned = allowance.assignments?.personnel?.some(id => id.toString() === employeeId.toString());
    
    try {
      if (isAssigned) {
        await removePersonnelAssignment(allowance._id, employeeId);
        enqueueSnackbar('Allowance unassigned from employee', { variant: 'success' });
      } else {
        await assignToPersonnel(allowance._id, [employeeId]);
        enqueueSnackbar('Allowance assigned to employee', { variant: 'success' });
      }
      await loadAllowances();
      await loadEmployeeAllowances(employeeId);
    } catch (error) {
      console.error('Error toggling allowance assignment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update assignment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const getPositionColor = (position) => {
    switch (position) {
      case 'Manager': return { bg: '#dcfce7', color: '#166534' };
      case 'Supervisor': return { bg: '#fef3c7', color: '#d97706' };
      case 'Cashier': return { bg: '#dbeafe', color: '#1d4ed8' };
      case 'Crew': return { bg: '#f3e8ff', color: '#7c3aed' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="200px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="300px" height={20} sx={{ mb: 3 }} />
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="120px" height={36} />
          </Box>
          {[...Array(6)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={70} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <PersonIcon sx={{ color: '#12C1DC' }} />
              Personnel Management
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{ 
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/masterfiles">
              Masterfiles
            </Link>
            <Typography color="text.primary">Personnel</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Store Selector, Date Range and View Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                  Select Store
                </Typography>
                <Autocomplete
                  value={selectedStore}
                  onChange={(event, newValue) => setSelectedStore(newValue)}
                  options={stores}
                  loading={storesLoading}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Sparkle Star International - Inho..."
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': { borderColor: '#12C1DC' },
                          '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StoreIcon sx={{ color: '#12C1DC', fontSize: '1rem' }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.group} • {option.location}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                  From
                </Typography>
                <TextField
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                  To
                </Typography>
                <TextField
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151', visibility: 'hidden' }}>
                  Actions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleViewRecord} // ✅ just pass function, no braces
                    sx={{
                      bgcolor: '#12C1DC',
                      '&:hover': { bgcolor: '#0FABDB' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1
                    }}
                  >
                    View
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Controls and Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            {/* Table Controls */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Show
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(e.target.value);
                      setCurrentPage(1);
                    }}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    entries
                  </Typography>
                  
                  <TextField
                    placeholder="Search personnel..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': { borderColor: '#12C1DC' },
                        '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#6b7280' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {searchTerm && (
                    <Tooltip title="Clear search">
                      <IconButton
                        size="small"
                        onClick={clearFilters}
                        sx={{
                          color: '#6b7280',
                          '&:hover': { color: '#12C1DC', bgcolor: 'rgba(18, 193, 220, 0.1)' }
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <Tooltip title="Export CSV">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('CSV')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <ExportIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Excel">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Excel')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <Icon icon="mdi:microsoft-excel" width="18" height="18" style={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('Print')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export PDF">
                    <IconButton
                      size="small"
                      onClick={() => handleExport('PDF')}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Table */}
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}>
              <Table
                stickyHeader
                size={isMobile ? "small" : "medium"}
                aria-label="personnel table"
              >
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableSortLabel
                          active={orderBy === 'name'}
                          direction={orderBy === 'name' ? order : 'asc'}
                          onClick={() => {
                            const isAsc = orderBy === 'name' && order === 'asc';
                            setOrder(isAsc ? 'desc' : 'asc');
                            setOrderBy('name');
                          }}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: '#12C1DC !important'
                            }
                          }}
                        >
                          Employee Name
                        </TableSortLabel>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{
                      fontWeight: 600,
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {paginatedPersonnel.map((person, index) => {
                      const isEvenRow = index % 2 === 0;
                      return (
                        <motion.tr
                          key={person._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                          style={{
                            display: 'table-row',
                            backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important'
                            },
                            '& > .MuiTableCell-root': {
                              borderRight: '1px solid #e0e0e0',
                              borderBottom: '1px solid #e0e0e0'
                            },
                            '& > .MuiTableCell-root:last-child': {
                              borderRight: 'none'
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                              {person.displayName}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  bgcolor: '#12C1DC',
                                  '&:hover': { bgcolor: '#0FABDB' },
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  px: 2,
                                  py: 0.5,
                                  minWidth: 'auto'
                                }}
                                onClick={() => handleDisplayEmployeeRecord(person)}
                              >
                                VIEW RECORD
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  bgcolor: '#3B82F6',
                                  '&:hover': { bgcolor: '#2563EB' },
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  px: 2,
                                  py: 0.5,
                                  minWidth: 'auto'
                                }}
                                onClick={() => handleSetRates(person)}
                              >
                                SET RATES
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  bgcolor: '#12C1DC',
                                  '&:hover': { bgcolor: '#0FABDB' },
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  px: 2,
                                  py: 0.5,
                                  minWidth: 'auto'
                                }}
                                onClick={() => handleOpenAllowancesDialog(person)}
                              >
                                MANAGE ALLOWANCES
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  bgcolor: '#EF4444',
                                  '&:hover': { bgcolor: '#DC2626' },
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  px: 2,
                                  py: 0.5,
                                  minWidth: 'auto'
                                }}
                                onClick={() => handleDeleteRates(person)}
                              >
                                DELETE RATES
                              </Button>
                            </Box>
                          </TableCell>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              gap: { xs: 2, sm: 0 }
            }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, filteredPersonnel.length)} of {filteredPersonnel.length} entries
                {searchTerm && ` (filtered from ${personnel.length} total entries)`}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      backgroundColor: '#12C1DC',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0FABDB'
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </motion.div>

        {/* Record View Modal */}
        <RecordViewModal
          open={recordModalOpen}
          onClose={handleCloseRecordModal}
          employee={selectedEmployee}
        />

        {/* Set Rates Modal */}
        <SetRatesModal
          open={setRatesModalOpen}
          onClose={handleCloseSetRatesModal}
          employee={ratesEmployee}
          onSave={handleSaveRates}
        />

        {/* Delete Rates Dialog */}
        <DeleteRatesDialog
          open={deleteRatesDialogOpen}
          onClose={handleCloseDeleteRatesDialog}
          employee={deleteRatesEmployee}
          onConfirm={handleConfirmDeleteRates}
          loading={deletingRates}
        />
        {loadingSchedule && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <Box
              sx={{
                bgcolor: "white",
                p: 3,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <CircularProgress size={40} sx={{ color: "#12C1DC", mr: 2 }} />
              <Typography>Loading employee schedule...</Typography>
            </Box>
          </Box>
        )}
        <Dialog
          open={employeeDetailsOpen}
          onClose={() => setEmployeeDetailsOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogTitle sx={{
            bgcolor: '#12C1DC',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <PersonIcon />
            Employee Schedule - {selectedEmployee?.displayName}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {selectedEmployee && (
              <Box sx={{ p: 3 }}>
                {loadingSchedule ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
                    <Typography sx={{ ml: 2, color: '#6b7280' }}>Loading employee schedule...</Typography>
                  </Box>
                ) : (
                  <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <Box sx={{
                      p: 3,
                      bgcolor: '#f8f9fa',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151' }}>
                        Daily Work Records
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
                        Period: { dayjs(fromDate).format("MMM DD") } - { dayjs(toDate).format("MMM DD, YYYY") }
                      </Typography>
                    </Box>
                    
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                      <Table sx={{ minWidth: 1200 }}>
                        <TableHead sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 3,
                          bgcolor: 'background.paper',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <TableRow>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Start Shift</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>End Shift</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Time In</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Time Out</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Hours Worked</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Tardy (mins)</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Overtime (hr)</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Night Diff (hr)</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Rest Day (hr)</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Legal Holiday (hr)</TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold' }}>Special Holiday (hr)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {employeeSchedule.map((record, index) => {
                            const isEvenRow = index % 2 === 0;
                            const hasRemarks = record.remarks && record.remarks.trim() && record.remarks.toLowerCase() !== 'no remarks';
                            return (
                            <TableRow
                              key={index}
                              hover
                              sx={{
                                bgcolor: hasRemarks
                                  ? '#fecaca' // Light red for remarks
                                  : isEvenRow ? '#ffffff' : '#f8f9fa',
                                '&:hover': {
                                  bgcolor: hasRemarks
                                    ? '#fca5a5 !important' // More noticeable red on hover
                                    : isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important'
                                },
                                '& .MuiTableCell-root': {
                                  borderRight: '1px solid #e0e0e0',
                                  borderBottom: '1px solid #e0e0e0'
                                },
                                '& .MuiTableCell-root:last-child': {
                                  borderRight: 'none'
                                }
                              }}
                            >
                              <TableCell sx={{ textAlign: 'left' }}>{dayjs(record.date).format('YYYY-MM-DD')}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{record.from || '-'}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{record.to || '-'}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{record.timeIn || '-'}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{record.timeOut || '-'}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{formatNumber(record.hourswork || 0, 1)}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{formatNumber(record.hoursTardy || 0, 0)}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{formatNumber(record.overtime || 0, 1)}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{formatNumber(record.nightdiff || 0, 1)}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{formatNumber(record.rd || 0, 1)}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{formatNumber(record.legalholiday || 0, 1)}</TableCell>
                              <TableCell sx={{ textAlign: 'left' }}>{formatNumber(record.specialholiday || 0, 1)}</TableCell>
  
                            </TableRow>
                            );
                          })}
                          
                         
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {employeeSchedule.length === 0 && !loadingSchedule && (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          No schedule data found for this employee in the selected period.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmployeeDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Manage Allowances Dialog */}
        <Dialog
          open={allowancesDialogOpen}
          onClose={handleCloseAllowancesDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#f8f9fa', 
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600,
            color: '#374151'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ color: '#12C1DC' }} />
              {selectedEmployee ? `${selectedEmployee.displayName || selectedEmployee.name} - Allowances` : 'Manage Allowances'}
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAllowanceDialog('add')}
              sx={{
                bgcolor: '#12C1DC',
                '&:hover': { bgcolor: '#0FABDB' },
                textTransform: 'none'
              }}
            >
              Create Allowance
            </Button>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search allowances..."
              value={allowanceSearchTerm}
              onChange={(e) => setAllowanceSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
                endAdornment: allowanceSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setAllowanceSearchTerm('')}
                      sx={{ color: '#9ca3af' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2, width: '100%' }}
            />

            {loadingAllowances ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Active</strong></TableCell>
                      <TableCell><strong>Taxable</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Admin fee</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeAllowances
                      .filter(allowance => {
                        const searchLower = allowanceSearchTerm.toLowerCase();
                        return allowance.name.toLowerCase().includes(searchLower) ||
                          (allowance.description || '').toLowerCase().includes(searchLower);
                      })
                      .map((allowance) => {
                        const employeeId = selectedEmployee?._id || selectedEmployee?.id;
                        const isAssigned = employeeId && allowance.assignments?.personnel?.some(id => id.toString() === employeeId.toString());
                        return (
                          <TableRow key={allowance._id} hover>
                            <TableCell>{allowance.name}</TableCell>
                            <TableCell>₱{allowance.amount}</TableCell>
{/*                            <TableCell>
                              <Chip
                                label={allowance.type === 'fixed_amount' ? 'Fixed' : 'Rate'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.type === 'fixed_amount' ? '#e3f2fd' : '#f3e5f5',
                                  color: allowance.type === 'fixed_amount' ? '#1976d2' : '#7b1fa2'
                                }}
                              />
                            </TableCell>*/}
{/*                            <TableCell>
                              {allowance.type === 'fixed_amount'
                                ? `₱${(allowance.amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : `${(allowance.rate || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`
                              }
                            </TableCell>*/}
                            <TableCell>{allowance.description || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.is_active ? 'Yes' : 'No'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.is_active ? '#e3f2fd' : '#ffebee',
                                  color: allowance.is_active ? '#1976d2' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.is_taxable ? 'Yes' : 'No'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.is_taxable ? '#e3f2fd' : '#f3e5f5',
                                  color: allowance.is_taxable ? '#1976d2' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.type === 'billing' ? 'Less to billing' : allowance.type === 'gross_billing' ? 'Add to Gross billing' : 'Charge to client'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.type === 'billing' ? '#e3f2fd' : allowance.type === 'gross_billing' ? '#e8f5e9' : '#f3e5f5',
                                  color: allowance.type === 'billing' ? '#1976d2' : allowance.type === 'gross_billing' ? '#2e7d32' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={allowance.admin ? 'Yes' : 'No'}
                                size="small"
                                sx={{
                                  bgcolor: allowance.admin ? '#e3f2fd' : '#f3e5f5',
                                  color: allowance.admin ? '#1976d2' : '#7b1fa2'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenAllowanceDialog('edit', allowance)}
                                    sx={{ color: '#12C1DC' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteAllowance(allowance)}
                                    sx={{ color: '#ef4444' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {employeeAllowances.filter(allowance => {
                      const searchLower = allowanceSearchTerm.toLowerCase();
                      return allowance.name.toLowerCase().includes(searchLower) ||
                        (allowance.description || '').toLowerCase().includes(searchLower);
                    }).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No allowances found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAllowancesDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Allowance Form Dialog */}
        <Dialog
          open={allowanceDialogOpen}
          onClose={handleCloseAllowanceDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {allowanceDialogMode === 'add' ? 'Create New Allowance' : 'Edit Allowance'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Allowance Name"
                value={allowanceFormData.name}
                onChange={(e) => setAllowanceFormData({ ...allowanceFormData, name: e.target.value })}
                error={!!allowanceFormErrors.name}
                helperText={allowanceFormErrors.name}
                fullWidth
                required
                disabled={savingAllowance}
              />

              <TextField
                label="Amount"
                type="number"
                value={allowanceFormData.amount}
                onChange={(e) => setAllowanceFormData({ ...allowanceFormData, amount: parseFloat(e.target.value) || 0 })}
                error={!!allowanceFormErrors.amount}
                helperText={allowanceFormErrors.amount}
                fullWidth
                required
                disabled={savingAllowance}
                inputProps={{ min: 0, step: 0.01 }}
              />

              {/*<FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={allowanceFormData.type}
                  onChange={(e) => setAllowanceFormData({ ...allowanceFormData, type: e.target.value })}
                  label="Type"
                  disabled={savingAllowance}
                >
                  <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
                  <MenuItem value="rate_based">Rate Based</MenuItem>
                </Select>
              </FormControl>

              {allowanceFormData.type === 'fixed_amount' ? (
                <TextField
                  label="Amount"
                  type="number"
                  value={allowanceFormData.amount}
                  onChange={(e) => setAllowanceFormData({ ...allowanceFormData, amount: parseFloat(e.target.value) || 0 })}
                  error={!!allowanceFormErrors.amount}
                  helperText={allowanceFormErrors.amount}
                  fullWidth
                  required
                  disabled={savingAllowance}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              ) : (
                <TextField
                  label="Rate"
                  type="number"
                  value={allowanceFormData.rate}
                  onChange={(e) => setAllowanceFormData({ ...allowanceFormData, rate: parseFloat(e.target.value) || 0 })}
                  error={!!allowanceFormErrors.rate}
                  helperText={allowanceFormErrors.rate}
                  fullWidth
                  required
                  disabled={savingAllowance}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              )}*/}

              <TextField
                label="Description"
                value={allowanceFormData.description}
                onChange={(e) => setAllowanceFormData({ ...allowanceFormData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
                disabled={savingAllowance}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={allowanceFormData.is_active}
                    onChange={(e) => setAllowanceFormData({ ...allowanceFormData, is_active: e.target.checked })}
                    disabled={savingAllowance}
                  />
                }
                label="Active"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={allowanceFormData.is_taxable}
                    onChange={(e) => setAllowanceFormData({ ...allowanceFormData, is_taxable: e.target.checked })}
                    disabled={savingAllowance}
                  />
                }
                label="Taxable"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={allowanceFormData.admin}
                    onChange={(e) => {
                      const newAdmin = e.target.checked;
                      setAllowanceFormData({ 
                        ...allowanceFormData, 
                        admin: newAdmin,
                        // Reset type to billing if admin is turned off while gross_billing is selected
                        type: !newAdmin && allowanceFormData.type === 'gross_billing' ? 'billing' : allowanceFormData.type
                      });
                    }}
                    disabled={savingAllowance}
                  />
                }
                label="Admin fee"
              />
              <RadioGroup
                value={allowanceFormData.type}
                onChange={(e) =>
                  setAllowanceFormData({
                    ...allowanceFormData,
                    type: e.target.value,
                  })
                }
              >
                <FormControlLabel
                  value="client"
                  control={<Radio disabled={savingAllowance} />}
                  label="Charge to client"
                />
                <FormControlLabel
                  value="billing"
                  control={<Radio disabled={savingAllowance} />}
                  label="Less to billing"
                />
                {allowanceFormData.admin && (
                  <FormControlLabel
                    value="gross_billing"
                    control={<Radio disabled={savingAllowance} />}
                    label="Add to Gross billing"
                  />
                )}
              </RadioGroup>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAllowanceDialog}
              disabled={savingAllowance}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAllowanceFormSubmit}
              variant="contained"
              disabled={savingAllowance}
              sx={{
                bgcolor: '#12C1DC',
                '&:hover': { bgcolor: '#0FABDB' }
              }}
            >
              {savingAllowance ? 'Saving...' : (allowanceDialogMode === 'add' ? 'Create' : 'Update')}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
      
    </Fade>

    


  );
};

export default Personnel;
